import React, { useEffect, useState } from "react";
import {
    Download,
    HardDriveDownload,
    Terminal,
    Copy,
    Check,
    Loader2,
    AlertTriangle,
} from "lucide-react";
import PageHeader from "../components/common/PageHeader";
import EmptyState from "../components/common/EmptyState";
import useAlert from "../components/hook/useAlert";
import callApi from "../helpers/networkHelper";
import API_CONFIG from "../config/apiConstant";

const getReadableErrorMessage = (error) => {
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.message) return error.message;
    return "Something went wrong. Please try again.";
};

const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return "—";
    const units = ["B", "KB", "MB", "GB"];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
    }
    return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
};

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

/**
 * The checksum is the only way an operator can tell that what reached the
 * client machine is what the server sent, so it needs to be copyable — nobody
 * transcribes 64 hex characters by hand.
 */
const ChecksumRow = ({ sha256 }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(sha256);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard access is blocked outside a secure context; the digest
            // is still on screen to select manually.
        }
    };

    return (
        <div className="flex items-center gap-2 min-w-0">
            <span
                className="text-xs font-mono truncate"
                style={{ color: "var(--main-text-muted)" }}
                title={sha256}
            >
                SHA256: {sha256}
            </span>
            <button
                type="button"
                onClick={handleCopy}
                title="Copy checksum"
                aria-label="Copy checksum"
                className="shrink-0 p-1 rounded transition-colors"
                style={{ color: copied ? "var(--success)" : "var(--main-text-muted)" }}
            >
                {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
        </div>
    );
};

const BinaryCard = ({ binary, isDownloading, onDownload }) => (
    <div
        className="rounded-xl p-5 flex flex-col gap-4"
        style={{
            background: "var(--card-bg)",
            border: "1px solid var(--card-border)",
        }}
    >
        <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
                <div
                    className="rounded-lg p-2 shrink-0"
                    style={{ background: "var(--brand-light)", color: "var(--brand)" }}
                >
                    <Terminal size={20} />
                </div>
                <div className="min-w-0">
                    <h3
                        className="text-base font-semibold truncate"
                        style={{ color: "var(--main-text)" }}
                    >
                        {binary.displayName}
                    </h3>
                    <p
                        className="text-xs font-mono mt-0.5"
                        style={{ color: "var(--main-text-muted)" }}
                    >
                        {binary.fileName} · {binary.platform}
                    </p>
                </div>
            </div>

            <button
                type="button"
                onClick={() => onDownload(binary)}
                disabled={!binary.available || isDownloading}
                className={`
          shrink-0 inline-flex items-center gap-2 rounded-lg px-4 py-2
          text-sm font-medium transition-colors
          ${!binary.available || isDownloading ? "cursor-not-allowed opacity-60" : ""}
        `}
                style={{
                    background: binary.available ? "var(--brand)" : "var(--muted)",
                    color: binary.available ? "#fff" : "var(--main-text-muted)",
                }}
            >
                {isDownloading ? (
                    <Loader2 size={16} className="animate-spin" />
                ) : (
                    <Download size={16} />
                )}
                {isDownloading ? "Downloading…" : "Download"}
            </button>
        </div>

        <p className="text-sm" style={{ color: "var(--main-text-muted)" }}>
            {binary.description}
        </p>

        {binary.usage && (
            <div
                className="rounded-lg px-3 py-2 text-xs font-mono overflow-x-auto"
                style={{
                    background: "var(--sidebar-hover)",
                    color: "var(--main-text)",
                }}
            >
                $ {binary.usage}
            </div>
        )}

        {binary.available ? (
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pt-1">
                <span className="text-xs" style={{ color: "var(--main-text-muted)" }}>
                    {formatSize(binary.sizeBytes)} · Built {formatDate(binary.builtAt)}
                </span>
                {binary.sha256 && <ChecksumRow sha256={binary.sha256} />}
            </div>
        ) : (
            <div
                className="flex items-center gap-2 text-xs"
                style={{ color: "var(--danger)" }}
            >
                <AlertTriangle size={14} />
                Not built on the server — run the CMake build to make it available.
            </div>
        )}
    </div>
);

/**
 * Card-shaped placeholder. The shared PageSkelton draws a filter bar and an
 * eight-column table, which sets the wrong expectation for a page that is a
 * grid of cards.
 */
const DownloadsSkeleton = () => (
    <div className="p-4 sm:p-6 lg:p-8">
        <div
            className="animate-pulse h-8 w-64 rounded-md mb-3"
            style={{ background: "var(--card-border)" }}
        />
        <div
            className="animate-pulse h-4 w-96 max-w-full rounded-md mb-8"
            style={{ background: "var(--card-border)" }}
        />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            {Array.from({ length: 4 }).map((_, index) => (
                <div
                    key={index}
                    className="animate-pulse h-48 rounded-xl"
                    style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)" }}
                />
            ))}
        </div>
    </div>
);

const DownloadsPage = () => {
    const { showSuccess, showError } = useAlert();

    const [binaries, setBinaries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [downloadingKey, setDownloadingKey] = useState(null);

    useEffect(() => {
        const fetchBinaries = async () => {
            setIsLoading(true);
            try {
                const res = await callApi(
                    API_CONFIG.BINARY.LIST.METHOD,
                    API_CONFIG.BINARY.LIST.ENDPOINT
                );
                setBinaries(res.data.data || []);
            } catch (err) {
                showError(getReadableErrorMessage(err));
            } finally {
                setIsLoading(false);
            }
        };

        fetchBinaries();
        // Fetched once on mount; the catalog only changes when the server is
        // rebuilt.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDownload = async (binary) => {
        setDownloadingKey(binary.key);
        try {
            const endpoint = API_CONFIG.BINARY.DOWNLOAD.ENDPOINT.replace(":key", binary.key);
            const res = await callApi(
                API_CONFIG.BINARY.DOWNLOAD.METHOD,
                endpoint,
                null,
                { "Content-Type": "application/json" },
                null,
                "blob",
                API_CONFIG.DOWNLOAD_TIMEOUT
            );

            if (!res.data || res.data.size === 0) throw new Error("Empty response");

            const blob = new Blob([res.data], { type: "application/octet-stream" });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = binary.fileName;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);

            showSuccess(`${binary.displayName} downloaded successfully`);
        } catch (err) {
            showError(getReadableErrorMessage(err));
        } finally {
            setDownloadingKey(null);
        }
    };

    if (isLoading) return <DownloadsSkeleton />;

    const availableCount = binaries.filter((item) => item.available).length;

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <PageHeader
                icon={<HardDriveDownload size={28} />}
                title="Tools & Downloads"
                subtitle="Client-side utilities for collecting hardware fingerprints and verifying issued licenses."
                meta={
                    binaries.length
                        ? `${availableCount} of ${binaries.length} tools available`
                        : undefined
                }
            />

            {binaries.length === 0 ? (
                <EmptyState
                    icon={<HardDriveDownload size={28} />}
                    title="No tools published"
                    description="The server has no downloadable tools configured. Build the project or set BINARY_DIR on the backend."
                />
            ) : (
                <>
                    <div
                        className="mb-6 rounded-lg px-4 py-3 text-sm"
                        style={{
                            background: "var(--brand-light)",
                            color: "var(--main-text)",
                            border: "1px solid var(--card-border)",
                        }}
                    >
                        After downloading, make the file executable before running it:{" "}
                        <code className="font-mono">chmod +x &lt;file&gt;</code>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                        {binaries.map((binary) => (
                            <BinaryCard
                                key={binary.key}
                                binary={binary}
                                isDownloading={downloadingKey === binary.key}
                                onDownload={handleDownload}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default DownloadsPage;
